import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit
} from '@angular/core';
import {RESOURCES_DATA_SERVICE, ResourcesDataService} from '../../../services/resources/resources-data.service';
import {UserProfileShareService} from '../../../services/shared-service/user-profile-share.service';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import { DateTimeHelper } from '../../../helper/date-time.helper';

@Directive({
  selector: '[fiTranslation]',
  standalone: false
})
export class TranslationDirective implements OnInit, OnChanges, OnDestroy {

  constructor(
    private el: ElementRef,
    private readonly sharedProfileService: UserProfileShareService,
    @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
  ) {
  }

  private static translationsPromise: Promise<{ [key: string]: string }>;
  private static languageCode: string;
  private static readonly translationSubject: BehaviorSubject<{ [key: string]: string }> = new BehaviorSubject({});

  static data;
  private readonly PAGE_DOMAIN = 'CONTACTS_UI';
  private profileSubscription: Subscription;

  @Input('fiTranslation') textKey: string;
  @Input() isPLaceHolder: boolean;
  @Input() isTitle: boolean;
  @Input() isUpdate: boolean;
  @Input() hasHTMLTags: boolean;
  @Input() params?: {[key: string]: any};

  // If replaceKeys is true and textKey is something like "Some texts: %KEY1%, %KEY2%", it will be translated to "Some text: key 1 value, key2 value"
  @Input() replaceKeys: boolean;

  static getTranslations(): Observable<{ [key: string]: string }> {
    return TranslationDirective.translationSubject.asObservable();
  }

  ngOnInit(): void {
    this.profileSubscription = this.sharedProfileService.getProfile().subscribe(data => {
      if (data && TranslationDirective.translationsPromise) {
        if (TranslationDirective.languageCode && TranslationDirective.languageCode !== data.User.LanguageCode) {
          TranslationDirective.translationsPromise = null;
        }

        this.translate();
        TranslationDirective.languageCode = data.User.LanguageCode;
      }
    });
  }

  ngOnChanges(): void {
    this.translate();
  }

  ngOnDestroy(): void {
    this.profileSubscription?.unsubscribe();
  }

  static escapeKeys(text: string): string {
	return text?.replace(/(%+)(\w*)(%)/gm, (match => {
		const body = match.replace(/%+/gm, '');
		const prefixes = match.length - body.length - 1;
		return '%'.repeat(2 * prefixes) + body + '%';
	}));
  }

  private getTranslation(translations: { [key: string]: string }): string {
    if (this.replaceKeys) {
      const matches = [...this.textKey.matchAll(/(%+)([-\w]*)(%)/gm)].reverse();
      let translation = [...this.textKey];

      for (const match of matches) {
        let [wholeText, prefix, body, postfix] = match;

        // if prefix number is odd we should replace key
        if (body && prefix.length % 2) {
          postfix = '';
          body = translations[body] ?? body;
        }

        // merge pair %s
        prefix = '%'.repeat(Math.floor(prefix.length / 2));
        translation.splice(match.index, wholeText.length, ...(prefix + body + postfix));
      }

      return translation.join('');
    }

    let result = translations[this.textKey] ?? this.textKey;

    if (this.params) {
      for (const key in this.params) {
        result = result.replace(`:${key}`, this.params[key])
      }
    }

    return result;
  }

  private translate(): void {
    this.getTranslationsPromise().then(translations => {
      const translation = this.getTranslation(translations);
      if (this.isTitle) {
        this.el.nativeElement.dataset.matTooltip = translation;
      } else if (this.isPLaceHolder) {
        this.el.nativeElement.placeholder = translation;
      } else if (this.isUpdate) {
        this.handleUpdate(translation);
      } else {
        if (this.el.nativeElement instanceof HTMLInputElement || this.el.nativeElement instanceof HTMLTextAreaElement) {
          this.el.nativeElement.value = translation;
        } else if(this.hasHTMLTags) {
          this.el.nativeElement.classList.add('is-html-snippet');
          this.el.nativeElement.innerHTML = translation;
        }else {
          this.el.nativeElement.innerText = translation;
        }
      }
    });
  }

  private handleUpdate(translation: string): void {
    this.el.nativeElement.classList.add('is-update-snippet');

    if (translation === '-') {
      this.el.nativeElement.innerHTML = '';
      this.el.nativeElement.classList.add('hide-before');
    } else {
      this.el.nativeElement.innerHTML = translation;
    }
  }

  private getTranslationsPromise(): Promise<{ [key: string]: string }> {
    if (!TranslationDirective.translationsPromise) {
      TranslationDirective.translationsPromise = new Promise<{ [key: string]: string }>((resolve, reject) => {
        this.resourcesDataService.getResourcesTranslations(this.PAGE_DOMAIN).subscribe(({data, success}) => {
          if (success) {
            TranslationDirective.data = data;
            TranslationDirective.insertAdditionalTranslations(TranslationDirective.languageCode, data);
            resolve(data);
            TranslationDirective.translationSubject.next(data);
          } else {
            resolve({});
          }
        });
      });
    }

    return TranslationDirective.translationsPromise;
  }

  private static insertAdditionalTranslations(lang: string, translations: { [key: string]: string }): void {
    for (const monthIndex of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]) {
      translations[`_MONTH_${monthIndex}_SHORT`] = DateTimeHelper.getMonthName(monthIndex, lang);
    }

    for (const weekIndex of [0, 1, 2, 3, 4, 5, 6]) {
      translations[`_WEEK_DAY_${weekIndex}_SHORT`] = DateTimeHelper.getWeekDayName(weekIndex, lang);
    }
  }
}
